import React from 'react';
import { Box, Flex, Link, Text } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { faBooks, faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { formatDate } from 'src/utils/date';
import TextWithIcon from 'src/components/TextWithIcon';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBookSparkles,
  faHashtag
} from '@fortawesome/pro-light-svg-icons';
import { AdjudicationsDetailsTab } from '.';
import { useAdjudicationData } from 'src/contexts/AdjudicationDataProvider';
import { AdditionalInformation } from 'src/components/Submission/AdditionalInformation';
import { useQuery } from '@tanstack/react-query';
import { fetchReviewExtensionDeadline } from 'src/api/queries';
import { InAdjudicationSidePanel } from 'src/components/Adjudications/InAdjudicationSidePanel';

interface Props {
  onClose?: () => void;
  setActiveTab: (tab: AdjudicationsDetailsTab) => void;
}

export const AdjudicationSidebar: React.FC<Props> = ({
  onClose,
  setActiveTab
}) => {
  const {
    adjudication,
    submissionContext,
    workflowResponse,
    fieldsResponse,
    isInviteRequestAccepted
  } = useAdjudicationData();
  const { workflowData, manuscriptId } = workflowResponse;
  const title = fieldsResponse.fields['Title']?.value;

  const { data } = useQuery({
    queryKey: ['adjudication-extension-deadline', adjudication?.id],
    queryFn: () =>
      adjudication?.id
        ? fetchReviewExtensionDeadline(adjudication.id, 'expert')
        : null
  });

  const submissionDate = workflowData.states?.find(
    state => state.name === 'initial'
  )?.completedWith?.date;

  const selectedFocusIssue = submissionContext?.data?.focusIssues?.find(
    item => item.id === fieldsResponse.fields.FocusIssue?.value
  );

  const closeButton = onClose ? (
    <Flex marginBottom={3} alignItems="center" flexDirection="row">
      <ButtonText
        onClick={onClose}
        leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
      >
        {' '}
        All Adjudications
      </ButtonText>
    </Flex>
  ) : null;

  return (
    <>
      {/* ----- Early Assessment Sidebar ----- */}
      <Flex width="100%" flexDirection="column" padding={8} gap={6}>
        {/* --- Close Button --- */}
        {closeButton}

        {/* --- Submission Details --- */}
        <Flex flexDirection="column" gap={2}>
          <Box
            textStyle="headingItem"
            overflow="hidden"
            textOverflow="ellipsis"
            noOfLines={3}
          >
            <RichTextPreview value={title! as string} />
          </Box>

          <Flex gap={4} wrap={'wrap'}>
            <TextWithIcon
              icon={faCalendar}
              text={formatDate(new Date(submissionDate ?? ''))}
            />
            <TextWithIcon
              icon={faFileLines}
              text={adjudication?.caseData.submission.type}
            />
            <TextWithIcon
              icon={faBooks}
              text={submissionContext?.data.shortname.toUpperCase()}
            />
            <TextWithIcon icon={faHashtag} text={manuscriptId} />
          </Flex>
        </Flex>

        {selectedFocusIssue && (
          <Flex flexDirection="row" alignContent="center" gap={2}>
            <TextWithIcon icon={faBookSparkles} text={'Special Issue: '} />
            {selectedFocusIssue?.issueLink ||
            submissionContext?.data?.guidelineLink ? (
              <Link
                variant="primary"
                href={
                  selectedFocusIssue?.issueLink ||
                  submissionContext?.data?.guidelineLink
                }
                target="_blank"
              >
                {selectedFocusIssue.name}
              </Link>
            ) : (
              <Text textStyle="textContext">{selectedFocusIssue.name}</Text>
            )}
          </Flex>
        )}

        <Flex flexDirection="column" backgroundColor="surfacePrimaryFill">
          <InAdjudicationSidePanel
            submissionCurrentWorkflowState={
              adjudication?.submissionCurrentWorkflowState
            }
            deadlineRequested={
              adjudication?.workflow.deadlineExtensions?.status === 'requested'
            }
            remainingDeadlineAutoExtension={
              adjudication?.workflow.deadlineExtensions?.remainingAutoextensions
            }
            deadlineExtensionDetails={data}
          />
        </Flex>

        {isInviteRequestAccepted() && (
          <AdditionalInformation
            role="expert"
            workflowResponse={workflowResponse}
            setActiveTab={setActiveTab}
            entityType="adjudication"
          />
        )}
      </Flex>
    </>
  );
};
